import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AppNav from '../../components/appNav';
import TitleText from '../../components/TitleText';
import TablePayment from '../../components/TablePayment';
import placeholderImage from '../../assets/placeholder-image.jpeg';
import { api, baseURL } from '../../services/api';
import { maskCNPJ, maskCPF2 } from '../../utils/mask';
import { validateBr } from 'js-brasil';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`custom-tabpanel-${index}`}
      aria-labelledby={`custom-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3, width: '100%' }}>{children}</Box>
      )}
    </div>
  );
}

export default function EventDetailsCustom() {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [tabStep, setTabStep] = useState(0);
  const [eventFiltered, setEventFiltered] = useState([]);
  const [imageThumb, setImageThumb] = useState({
    thumb: placeholderImage,
    width: '600px',
    height: '400px'
  });
  const [loadError, setLoadError] = useState(false);
  const [company, setCompany] = useState('');
  const [payments, setPayments] = useState([]);
  const [openPayments, setOpenPayments] = useState(false);

  const columns = [
    { Header: '#', accessor: 'icon', width: '100%' },
    { Header: 'Boleto', accessor: 'external_id', width: '100%' },
    { Header: 'Nome', accessor: 'name', width: '100%' },
    { Header: 'Baixar', accessor: 'actions', width: '90%' }
  ];

  async function getEventById(id) {
    try {
      const eventById = await api.get(`/events/${id}?populate=*`);
      if (!eventById.data.isActive) {
        setLoadError(true);
        return;
      }

      setEventFiltered(eventById.data);
      setImageThumb({
        thumb: eventById.data?.thumbnail?.formats?.small?.url
          ? baseURL + eventById.data.thumbnail.formats.small.url
          : placeholderImage,
        width: eventById.data?.thumbnail?.formats?.small?.width || '600px',
        height: eventById.data?.thumbnail?.formats?.small?.height || '400px'
      });
    } catch (error) {
      setLoadError(true);
    }
  }

  useEffect(() => {
    if (eventId) {
      getEventById(eventId);
    }
  }, [eventId]);

  function handlePanel(_, newStep) {
    setTabStep(newStep);
  }

  function handleView({ url }) {
    window.open(url, '_blank');
  }

  async function handlePayment() {
    const digits = company.replace(/\D/g, '');
    const isCPF = digits.length <= 11;

    if (isCPF) {
      if (!validateBr.cpf(company)) {
        alert('CPF Inválido!');
        return;
      }
      try {
        const payment = await api.post('/order/payment/company', { data: { company, eventId } });
        if (payment.data.length === 0) {
        alert('Nenhum boleto encontrado para este CNPJ');
        return;
      }
      if (payment.data.length === 1) {
        window.open(payment.data[0].payment_url, '_blank');
        return;
      }
      payment.data.forEach(pay => {
        pay.name = pay.subscribers?.[0]?.name || '-';
      });
      setPayments(payment.data);
      setOpenPayments(true);
      } catch (error) {
        alert(error.response?.data?.error?.message || 'Erro ao buscar boleto');
      }
      return;
    }

    if (!validateBr.cnpj(company)) {
      alert('CNPJ Inválido!');
      return;
    }

    try {
      const payment = await api.post('/order/payment/company', { data: { company, eventId } });
      if (payment.data.length === 0) {
        alert('Nenhum boleto encontrado para este CNPJ');
        return;
      }
      if (payment.data.length === 1) {
        window.open(payment.data[0].payment_url, '_blank');
        return;
      }
      payment.data.forEach(pay => {
        pay.name = pay.subscribers?.[0]?.name || '-';
      });
      setPayments(payment.data);
      setOpenPayments(true);
    } catch (error) {
      alert(error.response?.data?.error?.message || 'Erro ao buscar boletos');
    }
  }

  return (
    <Box sx={{ width: '100vw', minHeight: '100vh', overflowX: 'hidden' }}>
      <AppNav />

      <Dialog
        onClose={() => setOpenPayments(false)}
        fullWidth
        maxWidth="lg"
        open={openPayments}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Boletos Aguardando Pagamento
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => setOpenPayments(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <TablePayment title="Boletos" variant="info" view={handleView} rows={payments} columns={columns} />
        </DialogContent>
      </Dialog>

      {eventFiltered.length === 0 ? (
        <Stack
          sx={{ display: 'flex', width: '100%', height: '80vh', justifyContent: 'center' }}
          direction="column"
          justifyContent="center"
          alignItems="center"
          spacing={2}
        >
          {loadError ? (
            <Alert sx={{ width: '50%' }} severity="error">
              Evento desativado ou erro ao carregar evento. Tente novamente mais tarde.
            </Alert>
          ) : (
            <CircularProgress />
          )}
        </Stack>
      ) : (
        <>
          <Paper elevation={3} sx={{ mx: 'auto', my: 1 }} style={{ width: imageThumb.width, height: imageThumb.height }}>
            <Box
              style={{
                background: `url(${imageThumb.thumb})`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                height: '100%',
                width: '100%'
              }}
            />
          </Paper>

          <Box sx={{ display: 'flex', width: '100%', bgcolor: 'background.paper', justifyContent: 'center' }}>
            <Tabs value={tabStep} onChange={handlePanel} variant="scrollable" scrollButtons>
              <Tab label="Apresentação" id="custom-tab-0" aria-controls="custom-tabpanel-0" />
              <Tab label="Comprar" id="custom-tab-1" aria-controls="custom-tabpanel-1" />
              <Tab label="2ª Via de Boleto" id="custom-tab-2" aria-controls="custom-tabpanel-2" />
            </Tabs>
          </Box>

          <Box sx={{ width: '100%', mb: 5, py: 4 }}>
            <TabPanel value={tabStep} index={0}>
              <TitleText>APRESENTAÇÃO</TitleText>
              <Typography
                variant="body1"
                sx={{ width: '80%', mt: 3, mb: 5, mx: 'auto' }}
                textAlign="justify"
                component="div"
                dangerouslySetInnerHTML={{ __html: eventFiltered.apresentation }}
              />
            </TabPanel>

            <TabPanel value={tabStep} index={1}>
              <TitleText>COMPRAR</TitleText>
              <Button
                variant="contained"
                sx={{
                  display: 'block',
                  mt: 5,
                  mx: 'auto',
                  borderRadius: 5,
                  textAlign: 'center',
                  maxWidth: { xs: '70%', sm: '40%' }
                }}
                onClick={() => navigate(`/company-subscribe/${eventId}`)}
              >
                <Typography variant="h6">Comprar</Typography>
              </Button>

              <Typography
                variant="body1"
                sx={{ width: '80%', mt: 3, mb: 5, mx: 'auto' }}
                textAlign="justify"
                component="div"
                dangerouslySetInnerHTML={{ __html: eventFiltered.terms }}
              />
            </TabPanel>

            <TabPanel value={tabStep} index={2}>
              <TitleText>2ª VIA DE BOLETO</TitleText>
              <TextField
                fullWidth
                sx={{ display: 'block', m: 5, width: '60%', mx: 'auto' }}
                id="CNPJ"
                label="CPF ou CNPJ"
                variant="outlined"
                name="document"
                value={company}
                onChange={e => {
                  const raw = e.currentTarget.value.replace(/\D/g, '');
                  setCompany(raw.length <= 11 ? maskCPF2(e.currentTarget.value) : maskCNPJ(e.currentTarget.value));
                }}
                inputProps={{ maxLength: 18 }}
              />
              <Button
                variant="contained"
                sx={{ display: 'block', width: '40%', mx: 'auto', borderRadius: 5 }}
                onClick={handlePayment}
              >
                <Typography variant="h6">BUSCAR</Typography>
              </Button>
            </TabPanel>
          </Box>
        </>
      )}
    </Box>
  );
}
